import { useCallback, useState, useEffect, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  selectHasPeerHandRaised,
  selectLocalPeer,
  selectPeers,
  useAVToggle,
  useHMSActions,
  useHMSStore,
  useDevices,
  DeviceType
} from "@100mslive/react-sdk";
import { BsMicFill, BsMicMuteFill, BsCameraVideoFill, BsCameraVideoOffFill } from "react-icons/bs";
import { FaHandPaper } from "react-icons/fa";
import { IoChevronDown } from "react-icons/io5";
import { HiMiniSpeakerWave } from "react-icons/hi2";
import { PiUsersThreeFill } from "react-icons/pi";

import { WorkshopContext } from "../../contexts/workshop.context";
import Popup from "../workshop/popup";
import { defaultRoles } from "../../util/workshop.util";
import { getInitials } from "../../util/general.util";

const CallControl = ({ containerClass = "" }) => {
  const { t } = useTranslation();
  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio, toggleVideo } = useAVToggle();
  const { allDevices, selectedDeviceIDs, updateDevice } = useDevices();
  const localPeer = useHMSStore(selectLocalPeer);
  const peers = useHMSStore(selectPeers);
  const hmsActions = useHMSActions();
  const isHandRaised = useHMSStore(selectHasPeerHandRaised(localPeer?.id));
  const { socket, user, connectedUsers, disconnectedUsers, allUsersWithMods, workshopDetails } =
    useContext(WorkshopContext);

  const [audioPermission, setAudioPermission] = useState("prompt");
  const [videoPermission, setVideoPermission] = useState("prompt");
  const [showTooltip, setShowTooltip] = useState({ audio: false, video: false });
  const [showDeviceMenu, setShowDeviceMenu] = useState({
    audio: false,
    video: false,
    audioOutput: false
  });
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [removeUserId, setRemoveUserId] = useState(null);

  const audioMenuRef = useRef(null);
  const videoMenuRef = useRef(null);
  const audioOutputMenuRef = useRef(null);
  const settingsMenuRef = useRef(null);

  const audioDevices = allDevices?.audioInput || [];
  const videoDevices = allDevices?.videoInput || [];
  const audioOutputDevices = allDevices?.audioOutput || [];

  const checkPermissions = async () => {
    try {
      const micPermission = await navigator.permissions.query({ name: "microphone" });
      const camPermission = await navigator.permissions.query({ name: "camera" });

      setAudioPermission(micPermission.state);
      setVideoPermission(camPermission.state);

      // Add listeners for permission changes
      micPermission.addEventListener("change", () => {
        setAudioPermission(micPermission.state);
      });

      camPermission.addEventListener("change", () => {
        setVideoPermission(camPermission.state);
      });
    } catch (err) {
      console.error("Error checking permissions:", err);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  // Close device menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (audioMenuRef.current && !audioMenuRef.current.contains(event.target)) {
        setShowDeviceMenu((prev) => ({ ...prev, audio: false }));
      }
      if (videoMenuRef.current && !videoMenuRef.current.contains(event.target)) {
        setShowDeviceMenu((prev) => ({ ...prev, video: false }));
      }
      if (audioOutputMenuRef.current && !audioOutputMenuRef.current.contains(event.target)) {
        setShowDeviceMenu((prev) => ({ ...prev, audioOutput: false }));
      }
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const requestAudioPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      // Recheck permissions after request
      const result = await navigator.permissions.query({ name: "microphone" });
      setAudioPermission(result.state);
      // If permission was granted, automatically enable audio
      if (result.state === "granted" && !isLocalAudioEnabled) {
        await toggleAudio();
      }
    } catch (err) {
      setAudioPermission("denied");
      console.error("Error requesting audio permission:", err);
    }
  };

  const handleVideoButtonClick = async () => {
    if (videoPermission === "granted") toggleVideo();
    else {
      await navigator.mediaDevices.getUserMedia({ video: true });
      const result = await navigator.permissions.query({ name: "camera" });
      setVideoPermission(result.state);
      if (result.state === "granted") toggleVideo();
    }
  };

  const toggleRaiseHand = useCallback(async () => {
    if (isHandRaised) {
      await hmsActions.lowerLocalPeerHand();
    } else {
      await hmsActions.raiseLocalPeerHand();
    }
  }, [hmsActions, isHandRaised]);

  const handleAudioDeviceChange = async (deviceId) => {
    try {
      await hmsActions.setAudioSettings({ deviceId });
      await updateDevice({ deviceId, deviceType: DeviceType.audioInput });
      setShowDeviceMenu((prev) => ({ ...prev, audio: false }));
    } catch (err) {
      console.error("Error switching audio device with setAudioSettings:", err);
    }
  };

  const handleVideoDeviceChange = async (deviceId) => {
    try {
      await hmsActions.setVideoSettings({ deviceId });
      await updateDevice({ deviceId, deviceType: DeviceType.videoInput });
      setShowDeviceMenu((prev) => ({ ...prev, video: false }));
    } catch (err) {
      console.error("Error switching video device with setVideoSettings:", err);
    }
  };

  const handleAudioOutputDeviceChange = (deviceId) => {
    updateDevice({ deviceId, deviceType: DeviceType.audioOutput });
    setShowDeviceMenu((prev) => ({ ...prev, audioOutput: false }));
  };

  const handleRemoveUser = (userId) => {
    if (userId === user?.id) return;
    if (socket) {
      socket.emit("removeUser", userId);
      setRemoveUserId(null);
    }
  };

  // Get all users from workshop, marked as connected or disconnected
  const getAllUsers = () => {
    if (!allUsersWithMods || allUsersWithMods.length === 0) return [];

    return allUsersWithMods.map((workshopUser) => {
      const isConnected =
        connectedUsers?.some((u) => u.id === workshopUser.id) ||
        peers.some(
          (peer) => peer.customerUserId === workshopUser.id || peer.name === workshopUser.name
        );
      const isDisconnected = disconnectedUsers?.some((u) => u.id === workshopUser.id);

      return {
        id: workshopUser.id,
        name: workshopUser.name,
        role: workshopUser.role,
        isConnected: isConnected && !isDisconnected
      };
    });
  };

  // Check if user is participant (for hiding Manage Users feature only)
  const isParticipant = user?.role === defaultRoles.participant;

  return (
    <div className={`w-full ${containerClass}`}>
      <div className="flex items-center justify-center px-2 sm:px-4 py-3 sm:py-4 bg-gray-250/95 backdrop-blur-md rounded-t-2xl">
        {/* All Controls on the Right */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Video Control */}
          <div className="relative" ref={videoMenuRef}>
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                  videoPermission === "granted"
                    ? isLocalVideoEnabled
                      ? "bg-green-500 border-2 border-green-500 text-white-100 hover:bg-green-600"
                      : "bg-red-500 border-2 border-red-500 text-white-100 hover:bg-red-600"
                    : "bg-gray-500 border-2 border-gray-400 text-gray-400 cursor-not-allowed"
                }`}
                onClick={handleVideoButtonClick}
                onMouseEnter={() =>
                  videoPermission !== "granted" && setShowTooltip({ ...showTooltip, video: true })
                }
                onMouseLeave={() => setShowTooltip({ ...showTooltip, video: false })}>
                {isLocalVideoEnabled ? (
                  <BsCameraVideoFill className="text-base sm:text-xl p-2" />
                ) : (
                  <BsCameraVideoOffFill className="text-base sm:text-xl p-2" />
                )}
              </button>
              {
                <button
                  className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all duration-200 text-white-200 hover:text-white-100"
                  disabled={videoPermission !== "granted"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeviceMenu((prev) => ({
                      ...prev,
                      video: !prev.video,
                      audio: false,
                      audioOutput: false
                    }));
                  }}>
                  <IoChevronDown
                    className={`text-xs transition-transform ${showDeviceMenu.video ? "rotate-180" : ""}`}
                  />
                </button>
              }
            </div>
            {showTooltip.video && videoPermission !== "granted" && (
              <Tooltip message={t("callControl.videoPermission")} />
            )}
            {showDeviceMenu.video && videoDevices.length > 0 && (
              <DeviceMenu
                devices={videoDevices}
                onSelectDevice={handleVideoDeviceChange}
                type="video"
                selectedDeviceId={selectedDeviceIDs?.videoInput}
              />
            )}
          </div>

          {/* Audio Control */}
          <div className="relative" ref={audioMenuRef}>
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                  audioPermission === "granted"
                    ? isLocalAudioEnabled
                      ? "bg-green-500 border-2 border-green-500 text-white-100 hover:bg-green-600"
                      : "bg-red-500 border-2 border-red-500 text-white-100 hover:bg-red-600"
                    : "bg-gray-500 border-2 border-gray-400 text-gray-400 cursor-not-allowed"
                }`}
                onClick={audioPermission === "granted" ? toggleAudio : requestAudioPermission}
                onMouseEnter={() =>
                  audioPermission !== "granted" && setShowTooltip({ ...showTooltip, audio: true })
                }
                onMouseLeave={() => setShowTooltip({ ...showTooltip, audio: false })}>
                {isLocalAudioEnabled ? (
                  <BsMicFill className="text-base sm:text-xl p-2" />
                ) : (
                  <BsMicMuteFill className="text-base sm:text-xl p-2" />
                )}
              </button>
              {
                <button
                  className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all duration-200 text-white-200 hover:text-white-100"
                  disabled={audioPermission !== "granted"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeviceMenu((prev) => ({
                      ...prev,
                      audio: !prev.audio,
                      video: false,
                      audioOutput: false
                    }));
                  }}>
                  <IoChevronDown
                    className={`text-xs transition-transform ${showDeviceMenu.audio ? "rotate-180" : ""}`}
                  />
                </button>
              }
            </div>
            {showTooltip.audio && audioPermission !== "granted" && (
              <Tooltip message={t("callControl.audioPermission")} />
            )}
            {showDeviceMenu.audio && audioDevices.length > 0 && (
              <DeviceMenu
                devices={audioDevices}
                onSelectDevice={handleAudioDeviceChange}
                type="audio"
                selectedDeviceId={selectedDeviceIDs?.audioInput}
              />
            )}
          </div>

          {/* Speaker Control */}
          {audioOutputDevices.length > 0 && (
            <div className="relative" ref={audioOutputMenuRef}>
              <div className="flex items-center gap-0.5 sm:gap-1">
                <button className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200 border-2 border-white-100 text-white-100 hover:bg-white-100/10">
                  <HiMiniSpeakerWave className="text-base sm:text-xl p-2" />
                </button>
                <button
                  className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all duration-200 text-white-200 hover:text-white-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeviceMenu((prev) => ({
                      ...prev,
                      audioOutput: !prev.audioOutput,
                      audio: false,
                      video: false
                    }));
                  }}>
                  <IoChevronDown
                    className={`text-xs transition-transform ${showDeviceMenu.audioOutput ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              {showDeviceMenu.audioOutput && audioOutputDevices.length > 0 && (
                <DeviceMenu
                  devices={audioOutputDevices}
                  onSelectDevice={handleAudioOutputDeviceChange}
                  type="audioOutput"
                  selectedDeviceId={selectedDeviceIDs?.audioOutput}
                />
              )}
            </div>
          )}

          {/* Hand Raise */}
          <button
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200 border-2 ${
              isHandRaised
                ? "bg-yellow-500 border-yellow-500 text-white-100 hover:bg-yellow-600"
                : "bg-transparent border-white-100 text-white-100 hover:bg-white-100/10"
            }`}
            onClick={toggleRaiseHand}>
            <FaHandPaper className="text-base sm:text-xl p-2" />
          </button>

          {/* Admin Controls (Manage Users) - only for non-participants */}
          {!isParticipant && (
            <div className="relative" ref={settingsMenuRef}>
              <button
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200 border-2 border-white-100 text-white-100 hover:bg-white-100/10"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettingsMenu(!showSettingsMenu);
                  setShowDeviceMenu({ audio: false, video: false, audioOutput: false });
                }}>
                <PiUsersThreeFill className="text-base sm:text-xl p-2" />
              </button>
              {showSettingsMenu && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 min-w-[180px] bg-gray-250 rounded-lg shadow-xl border border-white-200/30 z-50 overflow-hidden backdrop-blur-md">
                  <button
                    className="w-full px-4 py-2.5 text-left text-sm text-white-100 hover:bg-orange-100/20 transition-colors font-medium"
                    onClick={() => {
                      setShowUserManagement(true);
                      setShowSettingsMenu(false);
                    }}>
                    {t("callControl.manageUsers")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {createPortal(
        <Popup open={showUserManagement} setOpen={setShowUserManagement} maxWidth="sm:max-w-md">
          <UserManagementModal
            users={getAllUsers()}
            currentUserId={user?.id}
            onRemoveUser={(userId) => setRemoveUserId(userId)}
          />
        </Popup>,
        document.body
      )}

      {createPortal(
        <Popup open={removeUserId} setOpen={setRemoveUserId} autoHeight={true}>
          <div className="text-center">
            <p className="text-xl font-bold text-black-100 mb-5">
              {t("callControl.removeUserConfirmation")}
            </p>
            <div className="w-full flex gap-4 mt-5">
              <button
                className="flex-1 px-4 py-2 bg-gray-200 text-black-100 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                onClick={() => setRemoveUserId(null)}>
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2 bg-rose-500 text-white-100 rounded-lg hover:bg-rose-600 transition-colors font-medium"
                onClick={() => handleRemoveUser(removeUserId)}>
                Remove
              </button>
            </div>
          </div>
        </Popup>,
        document.body
      )}
    </div>
  );
};

export default CallControl;

const Tooltip = ({ message }) => {
  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-250 text-white-100 text-xs rounded-lg whitespace-nowrap shadow-lg border border-white-200/30 z-50 backdrop-blur-md">
      {message}
    </div>
  );
};

const UserManagementModal = ({ users, currentUserId, onRemoveUser }) => {
  const { t } = useTranslation();
  return (
    <div className="w-full">
      <h2 className="text-xl font-bold text-black-100 mb-3">{t("callControl.manageUsers")}</h2>
      <div>
        {!users || users.length === 0 ? (
          <p className="text-black-100 text-center py-4">{t("callControl.noUsersFound")}</p>
        ) : (
          <div className="space-y-2">
            {users
              .sort((a, b) => (a.isConnected ? -1 : b.isConnected ? 1 : 0))
              .map((userItem) => {
                const isDisconnected = !userItem.isConnected;
                const isCurrentUser = userItem.id === currentUserId;
                const isModerator = userItem.role !== defaultRoles.participant;

                return (
                  <div
                    key={userItem.id}
                    className={`flex items-center justify-between p-2.5 rounded-lg shadow-md ${
                      isDisconnected ? "bg-gray-500/20 opacity-60" : ""
                    }`}>
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${
                          isModerator
                            ? "bg-orange-100 text-white-100"
                            : "bg-white-100 border-orange-100 border-2 text-black-100"
                        }`}>
                        <span className="text-xs font-medium uppercase">
                          {getInitials(userItem.name) ||
                            `${userItem.name?.charAt(0)}${userItem.name?.charAt(1)}`}
                        </span>
                      </div>
                      <div>
                        <p className={`text-sm font-medium text-black-100`}>{userItem.name}</p>
                        {isDisconnected && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t("callControl.disconnected")}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* {!isCurrentUser && onRemoveUser && isDisconnected && (
                      <button
                        className="w-7 h-7 p-1.5 cursor-pointer text-black-100 hover:text-rose-500 transition-colors"
                        onClick={() => onRemoveUser(userItem.id)}
                        title="Remove user">
                        <svg
                          className="w-full h-full"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )} */}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

const DeviceMenu = ({ devices, onSelectDevice, type, selectedDeviceId }) => {
  const getDeviceLabel = (device) => {
    if (device.label) {
      return device.label.replace(/^Default - /, "");
    }
    if (type === "audioOutput") {
      return `Speaker ${device.deviceId.slice(0, 8)}`;
    }
    return `${type === "audio" ? "Microphone" : "Camera"} ${device.deviceId.slice(0, 8)}`;
  };

  const getMenuTitle = () => {
    if (type === "audioOutput") return "Audio Output";
    if (type === "audio") return "Audio Input";
    return "Video Input";
  };

  const getSelectedDeviceLabel = () => {
    if (!selectedDeviceId) return null;
    const selectedDevice = devices.find((d) => d.deviceId === selectedDeviceId);
    if (selectedDevice) {
      return getDeviceLabel(selectedDevice);
    }
    return selectedDeviceId === "default" ? "Default" : selectedDeviceId.slice(0, 8);
  };

  return (
    <div className="absolute bottom-full left-0 mb-2 min-w-[200px] max-w-[300px] bg-gray-250 rounded-lg shadow-xl border border-white-200/30 z-50 overflow-hidden backdrop-blur-md">
      <div className="px-3 py-2 bg-orange-100 border-b border-white-200/30 text-xs font-semibold text-white-100">
        {getMenuTitle()}
        {selectedDeviceId && (
          <div className="text-xs font-normal text-white-100/80 mt-1 truncate">
            Selected: {getSelectedDeviceLabel()}
          </div>
        )}
      </div>
      <div className="max-h-60 overflow-y-auto">
        {devices.map((device) => {
          const isSelected = device.deviceId === selectedDeviceId;
          return (
            <button
              key={device.deviceId}
              className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                isSelected
                  ? "bg-orange-100/30 text-white-100 font-medium"
                  : "text-white-100 hover:bg-white-200/10"
              }`}
              onClick={() => onSelectDevice(device.deviceId)}>
              <div className="flex items-center justify-between">
                <span className="truncate">{getDeviceLabel(device)}</span>
                {isSelected && (
                  <span className="ml-2 text-orange-100 text-xs flex-shrink-0">✓</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
